import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppearanceMenu, Button, SkipMainLink } from "@clones/ui";
import {
  AudioLines,
  ClipboardList,
  LogOut,
  Mic,
  Settings2,
} from "lucide-react";
import { auth } from "@/api/auth";
import { studio } from "@/api/studio";
import { errorMessage, setDraftAccount } from "@/api/client";
import { allowedStudio, useCurrentUser } from "@/auth/queries";
import {
  CaptureProvider,
  useCaptureManager,
  useCaptureSnapshot,
} from "@/capture/context";
import type { Account } from "@/api/types";
import { duration } from "@/lib/format";
function Workspace({
  account,
  connectionWarning,
}: {
  account: Account;
  connectionWarning: boolean;
}) {
  const manager = useCaptureManager();
  const snapshot = useCaptureSnapshot();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const profile = useQuery({
    queryKey: ["studio-profile", account.id],
    queryFn: studio.profile,
    staleTime: 60_000,
  });
  const active = snapshot.captures.find((c) => c.id === snapshot.activeId);
  const logout = async () => {
    setBusy(true);
    setError(null);
    try {
      await manager?.prepareLogout();
      await auth.logout();
      setDraftAccount(null);
      qc.clear();
      await navigate({ to: "/login", search: { redirect: undefined } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <SkipMainLink />
      <div className="studio-layout">
        <aside className="studio-sidebar">
          <Link to="/" className="studio-brand">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
              <AudioLines className="h-5 w-5" />
            </span>
            <span>
              Research
              <br />
              <span className="text-xs font-normal text-muted-foreground">
                Studio
              </span>
            </span>
          </Link>
          <nav className="studio-nav" aria-label="Навигация Студии">
            <Link to="/" activeOptions={{ exact: true }}>
              <ClipboardList className="h-4 w-4" />
              Интервью
            </Link>
            <Link to="/profile">
              <Settings2 className="h-4 w-4" />
              Настройки
            </Link>
          </nav>
          <div className="studio-sidebar-footer mt-auto text-xs leading-relaxed text-muted-foreground">
            После интервью проверьте и подтвердите материал.
          </div>
        </aside>
        <div className="min-w-0">
          <header className="studio-topbar">
            <span className="text-sm text-muted-foreground">
              {account.first_name || account.username}
            </span>
            <div className="flex items-center gap-2">
              <AppearanceMenu />
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void logout()}
              >
                <LogOut className="h-4 w-4" />
                Выйти
              </Button>
            </div>
          </header>
          <main id="main-content" tabIndex={-1} className="studio-content">
            {connectionWarning ? (
              <p role="status" className="mb-3 rounded-md border p-3 text-sm">
                Не удалось обновить доступ на сервере. Локальная запись
                продолжается под исходной учётной записью; для передачи данных
                может потребоваться повторный вход.
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="mb-3 text-sm text-destructive">
                {error}
              </p>
            ) : null}
            {active ? (
              <Link
                to="/tasks/$taskId"
                params={{ taskId: active.taskId }}
                role="status"
                className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-destructive bg-card p-3 text-sm"
              >
                <Mic className="h-4 w-4 text-destructive" />
                Идёт запись: {active.taskTitle} ·{" "}
                {duration(snapshot.receivedFrames / active.sampleRate)}
                <span className="ml-auto text-xs">Вернуться к записи →</span>
              </Link>
            ) : null}
            {profile.data && !profile.data.onboarding_completed_at ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card p-3 text-sm">
                <span>
                  Первое интервью? Подготовьте запись и настройте голосовой
                  профиль.
                </span>
                <Link
                  className="font-medium text-primary underline"
                  to="/profile"
                >
                  Краткое введение
                </Link>
              </div>
            ) : null}
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
export function AppShell() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  useEffect(() => {
    if (user.isSuccess && user.data === null)
      void navigate({ to: "/login", search: { redirect: undefined } });
  }, [navigate, user.data, user.isSuccess]);
  if (user.isPending)
    return (
      <div className="studio-auth">
        <p role="status">Проверяем доступ…</p>
      </div>
    );
  if (user.isError && !user.data)
    return (
      <div className="studio-auth">
        <div>
          <p role="alert">{errorMessage(user.error)}</p>
          <Button className="mt-3" onClick={() => void user.refetch()}>
            Повторить
          </Button>
        </div>
      </div>
    );
  if (!user.data) return null;
  if (!allowedStudio(user.data))
    return (
      <div className="studio-auth">
        <div className="studio-panel max-w-md">
          <h1 className="text-xl font-semibold">Нужен доступ интервьюера</h1>
          <p className="mt-3 text-sm">
            Эта учётная запись не имеет доступа в Research Studio. Обратитесь к
            администратору.
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() =>
              void auth.logout().then(() => {
                setDraftAccount(null);
                location.assign("/login");
              })
            }
          >
            Выйти
          </Button>
        </div>
      </div>
    );
  return (
    <CaptureProvider key={user.data.id} accountId={user.data.id}>
      <Workspace account={user.data} connectionWarning={user.isError} />
    </CaptureProvider>
  );
}
